'use client'

import { StytchB2BProvider } from '@stytch/nextjs/b2b'
import { usePathname } from 'next/navigation'
import { stytchClient } from '@/utilities/stytch/stytchClient'
import { isGoogleOAuthPopupCallbackPath } from '@/utilities/paths'
import { ReactNode } from 'react'
import { UserProvider } from '@/providers/UserContext'
import { LaunchDarklyProvider } from '@/providers/LaunchDarkly'
import { PhiMaskDocumentMetadata } from '@/components/PhiMask/PhiMaskDocumentMetadata'
import AshAPIInit from '@/utilities/debug/AshAPIInit'

interface StytchAuthProviderProps {
  children: ReactNode
}

function useIsGoogleOAuthPopupCallback(): boolean {
  const pathname = usePathname() ?? ''
  return isGoogleOAuthPopupCallbackPath(pathname)
}

export const StytchAuthProvider = ({ children }: StytchAuthProviderProps) => {
  const skipStytch = useIsGoogleOAuthPopupCallback()

  // The Google callback popup only forwards a token to the opener. Skip Stytch
  // so the SDK does not fetch session/RBAC and then abort when the window closes.
  if (skipStytch) {
    return children
  }

  return <StytchB2BProvider stytch={stytchClient}>{children}</StytchB2BProvider>
}

export function StytchDependentProviders({ children }: Readonly<{ children: ReactNode }>) {
  const skipStytch = useIsGoogleOAuthPopupCallback()
  if (skipStytch) {
    return children
  }

  return (
    <UserProvider>
      <LaunchDarklyProvider>
        <PhiMaskDocumentMetadata />
        <AshAPIInit />
        {children}
      </LaunchDarklyProvider>
    </UserProvider>
  )
}

export default StytchAuthProvider
